import { useMemo, useState } from "react";
import { CalcSlider, CalcDonut, StatPill, fmtINR } from "./CalcShared";
import { CALC } from "@/constants/testIds";

// Step-up SIP: monthly SIP increases by stepPct each year
function stepUpFV(monthlyStart, years, ratePct, stepPct) {
  const r = ratePct / 12 / 100;
  let monthly = monthlyStart;
  let fv = 0;
  let invested = 0;
  for (let y = 0; y < years; y++) {
    // FV after 12 months of investing `monthly` each month, then grown for remaining years
    for (let m = 0; m < 12; m++) {
      fv = fv * (1 + r) + monthly;
      invested += monthly;
    }
    monthly = monthly * (1 + stepPct / 100);
  }
  return { fv: Math.round(fv), invested: Math.round(invested) };
}

export default function StepUpSIPCalculator() {
  const [amount, setAmount] = useState(10000);
  const [years, setYears] = useState(15);
  const [rate, setRate] = useState(12);
  const [step, setStep] = useState(10);

  const result = useMemo(() => {
    const { fv, invested } = stepUpFV(amount, years, rate, step);
    return { invested, future: fv, gain: fv - invested };
  }, [amount, years, rate, step]);

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid={CALC.stepUpCard}>
      <div className="space-y-6">
        <CalcSlider
          label="Starting monthly SIP"
          value={amount}
          onChange={setAmount}
          min={500}
          max={100000}
          step={500}
          format={fmtINR}
          testId={CALC.stepAmount}
        />
        <CalcSlider
          label="Annual step-up"
          value={step}
          onChange={setStep}
          min={0}
          max={25}
          step={1}
          format={(v) => `${v}%`}
          testId={CALC.stepIncrement}
        />
        <CalcSlider
          label="Investment period"
          value={years}
          onChange={setYears}
          min={1}
          max={35}
          step={1}
          format={(v) => `${v} yr`}
          testId={CALC.stepYears}
        />
        <CalcSlider
          label="Expected annual return"
          value={rate}
          onChange={setRate}
          min={4}
          max={20}
          step={0.5}
          format={(v) => `${v}%`}
          testId={CALC.stepRate}
        />
      </div>

      <div className="bg-muted/40 rounded-2xl p-6">
        <CalcDonut invested={result.invested} returns={result.gain} />
        <div className="grid grid-cols-3 gap-3 mt-4" data-testid={CALC.stepResult}>
          <StatPill label="Invested" value={fmtINR(result.invested)} />
          <StatPill label="Returns" value={fmtINR(result.gain)} tone="secondary" />
          <StatPill label="Future Value" value={fmtINR(result.future)} tone="primary" />
        </div>
        <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
          Step-up SIPs grow with your income. Even a 10% annual increase can dramatically improve the corpus.
        </p>
      </div>
    </div>
  );
}
